import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GoldSilverInventorySelectedTab, ReportGoldInventoryReceivedListingFilters, ReportsGoldInventorySelectedTab, TGoldReduxState } from '~types/redux/state';
import {
    TBuyOrSellMultipleGoldParams,
    TCreateGoldConversionReduxParams,
    TCreateInventoryParamsV2,
    TCreateTransactionDraftReduxParams,
    TCreateTransactionDraftResponse,
    TGetGoldConversionAmountParams,
    TGetGoldPricesAPIResponse,
    TGetInventoryFilterAPIResponse,
    TGetInventoryFilterParams,
    TGetInventoryListingAPIResponse,
    TGetInventoryListingFilters,
    TGetInventoryTransactionListingAPIResponse,
    TGetInventoryTransactionListingFilters,
    TGetInventoryTransferListingAPIResponse,
    TGetInventoryTransferListingFilters,
    TGetProductFilterAPIResponse,
    TGetSerialNumberFilterAPIResponse,
    TGetSerialNumberFilterParams,
    TGetSerialNumberListingFilter,
    TGetSerialNumberListingAPIResponse,
    TGetTotalNetValueOrQuantityReduxParams,
    TGetTransactionListingAPIResponse,
    TGetTransactionListingFilters,
    TGetTransactionPricePerGramValueReduxParam,
    TGoldConversionFiltersRedux,
    TGoldConversionListingAPIResponse,
    TGoldTransactionListing,
    TInventoryTransactionListing,
    TTransactionListing,
    TTransferInventoryParams,
    TUpdateEarmarkDetailsReduxParam,
    TCreateEarmarkConversionReduxParams,
    TGetEarmarkConversionListingAPIResponse,
    TGetEarmarkConversionListingFilters,
    TGetEarmarkConversionListingReduxParams,
    TGetInventoryUsageAPIResponse,
    TGetInventoryTransfersAPIResponse,
    TInventoryTransferData,
    TGetInventoryFilterAPIParams,
    TGetBranchMarketStatusBranchAPIResponse,
    TCreateConversionDraftsAPIResponse,
    TGetConversionDraftsFeeAPIResponse,
    TGetConversionDraftsFeeAPIParams,
    TGetConversionDraftsFeeReduxParams,
    TCreateConversionFinalDraftsParams,
    TGetConversionFinalDraftsSummaryAPIResponse,
    TGetCancelAffectedTransactionAPIResponse,
    TGetTransactionBankBuySerialNumberDetailsParams,
    TGetTransactionBankBuySerialNumberDetailsAPIResponse,
    TGetTransactionBankBuySerialNumberDetailsBatchParams,
    TGoldConversionListingData,
} from '~types/feat/gold';
import { FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum, MarketStatusEnum, TransactionActionEnum, TransactionStatusEnum, TransferStatusEnum, GoldConversionStatusEnum } from '~utils/enums';
import { TUpdateInventoryTransactionStatusReduxParams, TUpdateTransactionStatusReduxParams, TupdateTransferStatusReduxParams } from '~types/feat/approval';
import dayjs from 'dayjs';
import { TCreateStockOutParams, TGetStockOutsAPIResponse, TGetStockOutsListingFilters, TgetStockOutTransactionsListingAPIResponse, TGetStockOutTransactionsListingParams } from '~types/feat/stockOut';

const getFirstDayOfMonth = () => {
    const today = dayjs();

    const firstDay = today.startOf('month');

    return firstDay.toISOString();

};

const getTodaysDate = () => {
    const today = dayjs().endOf('day').toISOString();

    return today;
};

export const initialState: TGoldReduxState = {
    loadings: {
        getAllProducts: false,
        getAllInventories: false,
        getAllSerialNumbers: false,
        getBranchInventories: false,
        getMultipleSerialNumbers: false,
        // buy and sell gold
        getTransactionListing: false,
        getGoldPrices: false,
        createBuyGoldTransaction: false,
        createSellGoldTransaction: false,
        getTransactionReceipt: false,
        getTransactionPricePerGramValue: false,
        getCancelAffectedTransaction: false,
        getTransactionBankBuySerialNumberDetails: false,
        // gold inventory
        getInventoryTransactionListing: false,
        getInventoryListing: false,
        createInventory: false,
        createTransactionDraft: false,
        transferInventory: false,
        exportInventoryTransactionListing: false,
        exportGoldTransactionListing: false,
        updateInventoryTransactionStatus: false,
        updateTransactionStatus: false,
        getTotalNetValueOrQuantity: false,
        // transfers
        getInventoryTransferListing: false,
        updateInventoryTransferStatus: false,
        getSerialNumberListing: false,
        //gold inventory report
        getGoldInventoryReportReceivedListing: false,
        exportInventoryReceivedListing: false,
        getGoldInventoryReportDeliveredListing: false,
        exportInventoryDeliveredListing: false,
        getBranchInventoryListing: false,
        //Conversion
        getGoldConversionListing: false,
        getGoldConversionAmount: false,
        createConversionDrafts: false,
        getConversionDraftsFee: false,
        createConversionFinalDrafts: false,
        getConversionFinalDraftsSummary: false,
        createGoldConversion: false,
        updateEarmarkDetails: false,
        createEarmarkConversion: false,
        getEarmarkConversionListing: false,
        getInventoryUsage: false,
        getSelectedCustomerConversion: false,
        // Stock Out
        getStockOutListing: false,
        createStockOut: false,
        getStockOutTransactionsListing: false,
        // market status
        getBranchMarketStatus: false,
    },
    buyAndSellGold: {
        transactionListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                name: '',
                idNo: '',
                action: '',
                status: '',
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                branchName: '',
                metalType: '',
                branchCode: '',
                silverType: '',
                goldType: '',
            },
            data: [],
        },
        goldPrices: {
            gbuy: '',
            gsell: '',
            proof: {
                crc: '',
                dsig: '',
            },
            validFrom: '',
            validUntil: '',
            epoch: 0,
            origin: '',
        },
        transactionType: TransactionActionEnum['Bank Sell'],
        selectedTransaction: undefined,
        cancelAffectedTransaction: {
            totalCount: 0,
            data: [],
        },
        transactionBankBuySerialNumberDetails: {
            serialNo: '',
            itemCode: null,
            productId: '',
            productName: '',
            physicalGoldWeight: '',
            physicalSilverWeight: '',
            metalType: MetalTypeEnum.Gold,
            goldType: GoldTypeEnum.PaperGold,
            silverType: SilverTypeEnum.PaperSilver,
            fundType: FundTypeEnum.Conventional,
            isThirdParty: true,
        }
    },
    goldInventory: {
        inventoryTransactionListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchCode: '',
                branchName: '',
                productId: 0,
                status: '',
                productName: '',
                action: '',
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                metalType: '',
            },
            data: [],
        },
        inventoryListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchName: '',
                branchCode: '',
                goldType: GoldTypeEnum.PaperGold,
                metalType: MetalTypeEnum.Gold,
            },
            data: [],
            selectedTab: GoldSilverInventorySelectedTab.GoldIslamicPhysical,
        },
        inventoryTransferListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchFrom: 0,
                branchTo: 0,
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
            },
            data: [],
        },
        inventoryStockOutListing: {
            index: 0,
            maxIndex: 0,
            totalCount: 0,
            filters: {
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                status: TransactionStatusEnum.Pending,
                metalType: '',
                branchCode: '',
                branchName: '',
            },
            stockOuts: [],
        },
        inventoryStockOutTransactionsListing: {
            affectedTransactions: [],
            affectedEarmarks: [],
            affectedConversions: [],
            affectedCollaterals: [],
            affectedEarmarkConversions: [],
        },
    },
    goldInventoryReport: {
        selectedTab: ReportsGoldInventorySelectedTab.BranchInventory,
        branchInventory: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchCode: '',
            },
            data: [],

        },
        inventoryReceived: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchName: '',
                branchCode: '',
                fundType: '',
                productName: '',
                productId: 0,
                serialNo: '',
            },
            data: [],
        },
        inventoryDelivered: {
            index: 0,
            maxIndex: 0,
            filters: {
                branchName: '',
                branchCode: '',
                fundType: '',
                productName: '',
                productId: 0,
                serialNo: '',
            },
            data: [],
        },
    },
    serialNumber: {
        serialNumberListing: {
            index: 0,
            maxIndex: 0,
            filters: {
                // serialNo: '',
                metalType: '',
                index: 0,
                serialNo: undefined,
            },
            data: [],
        },
    },
    allProducts: [],
    allInventories: [],
    allSerialNumbers: [],
    multipleSerialNumbers: [],
    branchInventories: [],
    createTransactionSummary: {
        draftId: 0,
        customerId: 0,
        action: TransactionActionEnum['Bank Sell'],
        baseAPIPrice: 0,
        items: [{
            createdAt: '',
            updatedAt: '',
            id: 0,
            draftId: 0,
            productId: 0,
            goldPrice: 0,
            amount: '',
            totalNetValue: 0,
            baseAPIPrice: 0,
            action: TransactionActionEnum['Bank Sell'],
            userId: 0,
            totalNetValueWithoutDiscount: 0,
            serialNo: [],
        }],
    },
    params: {
        buyOrSellGoldParams: {
            customerId: 0,
            name: '',
            idNo: '',
            CIFNo: '',
            branchName: '',
            branchCode: '',
            baseAPIPrice: '',
            region: '',
            metalType: MetalTypeEnum.Gold,
            products: [{
                productId: 0,
                productName: '',
                goldType: GoldTypeEnum.PaperGold,
                silverType: SilverTypeEnum.PaperSilver,
                availableQuantity: 'Please select customer first',
                collateralQuantity: 'Please select customer first',
                amount: '',
                serialNo: '',
                goldAmountMYR: '',
                totalNetValue: '',
                discountId: 0,
                discountCode: '',
                discountName: '',
            }],
            fundType: FundTypeEnum.Islamic,
            salesPersonnelName: '',
            referralName: '',
            isEarmarkTransaction: false,
            earmarkEndDate: undefined,
            projectName: undefined,
        },
        goldConversionDraftsFeeParams: {
            draftId: 0,
            originProductId: 0,
            targetProductId: 0,
            quantity: 0,
        },
        goldConversionFinalDraftsParams: {
            id: 0,
            customerId: 0,
            branchId: 0,
            originProductId: 0,
            targetProductId: 0,
            quantity: '',
            serialNoIds: [],
            salesPersonnelName: '',
            referralName: '',
            metalType: MetalTypeEnum.Gold,
        },
        goldConversionParams: {
            draftId: 0,
        },
        createInventoryParams: {
            branchName: '',
            branchCode: '',
            products: [{
                productId: 0,
                productName: undefined,
                amount: 0,
                serialNo: '',
            }],
            metalType: MetalTypeEnum.Gold,
        },
        transferInventoryParams: {
            branchToCode: '',
            branchToName: '',
            branchFromCode: '',
            branchFromName: '',
            productId: 0,
            productName: '',
            amount: '',
            serialNo: [],
            metalType: MetalTypeEnum.Gold,
        },
        inventoryFilterParams: {
            branchCode: '002',
            branchName: '',
            metalType: MetalTypeEnum.Gold,
            goldType: GoldTypeEnum.PaperGold,
            silverType: SilverTypeEnum.PaperSilver,
        },
        serialNumberFilterParams: {
            action: undefined,
            branchCode: undefined,
            customerId: undefined,
        },
        stockOutsFilterParams: {
            metalType: MetalTypeEnum.Gold,
            dateFrom: '',
            dateTo: '',
            status: TransactionStatusEnum.Pending,
        },
        createStockOutParams: {
            productId: 0,
            amount: 0,
            branchId: 0,
            serialNoId: [],
        },
        stockOutTransactionsParams: {
            productId: 0,
            amount: 0,
            serialNoId: [],
        },
    },
    goldConversionDraftId: {
        draftId: 0,
    },
    goldConversionDraftsFee: {
        conversionFeePerGram: 0,
        weightedConversionFee: 0,
    },
    goldConversionFinalDraftsSummary: {
        branchCode: '',
        branchName: '',
        regionName: '',
        customerName: '',
        cifNo: '',
        identificationNo: '',
        quantity: 0,
        targetProductName: '',
        conversionFee: 0,
        conversionStatus: GoldConversionStatusEnum.Pending,
        description: [],
        serialNo: [],
    },
    goldConversion: {
        index: 0,
        maxIndex: 0,
        data: [],
        filters: {
            customerName: '',
            idNo: '',
            branchName: '',
            status: '',
            dateTo: '',
            dateFrom: '',
            metalType: '',
        },
        goldConversionAmount: undefined,
    },
    selectedConversion: undefined,
    selectedCustomerConversionDetails: undefined,
    earmarkConversionListing: {
        index: 0,
        maxIndex: 0,
        filters: {
            branchId: '',
            customerName: '',
            idNo: '',
            branchName: '',
            status: '',
            dateFrom: '',
            dateTo: '',
            metalType: '',
        },
        data: [],
    },
    inventoryUsage: {
        totalInventoryStock: 0,
        totalInventoryUsed: 0,
        totalInventoryBalance: 0,
    },
    goldBranchMarketStatusDetails: {
        branchId: 0,
        branchCode: '',
        status: MarketStatusEnum.Open,
        currentTime: '',
    },
    modals: {
        isFormFilled: false,
        isConfirmationModalOpen: false,
        isBuyOrSellGoldModalOpen: false,
        isTransactionDetailsModalOpen: false,
        isInventoryListingModalOpen: false,
        isCreateInventoryModalOpen: false,
        isTransferInventoryModalOpen: false,
        isCreateGoldConversionModalOpen: false,
        isCreateStockOutModalOpen: false,
        isStockOutTransactionsModalOpen: false,
        isConversionDetailsModalOpen: false,
        confirmUpdateInventoryTransactionStatusModalIsOpen: false,
        confirmUpdateTransactionStatusModalIsOpen: false,
        confirmUpdateCancelTransactionModalIsOpen: false,
        confirmUpdateInventoryTransfersStatusModalIsOpen: false,
        transactionProductListingModalIsOpen: false,
        conversionListingDetailsModalIsOpen: false,
    },
    inventoryTransactionListingForExport: [],
    inventoryReceivedListingForExport: [],
    inventoryDeliveredListingForExport: [],
    goldTransactionListingForExport: [],
    tempUpdateInventoryTransactionState: {
        inventoryLogId: 0,
        status: TransactionStatusEnum.Pending,
    },
    tempUpdateTransactionState: {
        transactionId: 0,
        status: TransactionStatusEnum.Pending,
    },
    tempUpdateInventoryTransfersState: {
        id: 0,
        status: TransferStatusEnum.PendingDeliver,
    },
    errors: {
        getAllProducts: '',
        getAllInventories: '',
        getAllSerialNumbers: '',
        getBranchInventories: '',
        getMultipleSerialNumbers: '',
        // buy and sell gold
        getTransactionListing: '',
        getGoldPrices: '',
        createBuyGoldTransaction: '',
        createSellGoldTransaction: '',
        getTransactionReceipt: '',
        getTransactionPricePerGramValue: '',
        getCancelAffectedTransaction: '',
        getTransactionBankBuySerialNumberDetails: '',
        // gold inventory
        getInventoryTransactionListing: '',
        getInventoryListing: '',
        createInventory: '',
        createTransactionDraft: '',
        transferInventory: '',
        exportInventoryTransactionListing: '',
        exportGoldTransactionListing: '',
        updateInventoryTransactionStatus: '',
        updateTransactionStatus: '',
        getTotalNetValueOrQuantity: '',
        // transfer
        getInventoryTransferListing: '',
        updateInventoryTransferStatus: '',
        getSerialNumberListing: '',
        //gold inventory report
        getGoldInventoryReportReceivedListing: '',
        exportInventoryReceivedListing: '',
        getGoldInventoryReportDeliveredListing: '',
        exportInventoryDeliveredListing: '',
        getBranchInventoryListing: '',
        // Conversion
        getGoldConversionListing: '',
        getGoldConversionAmount: '',
        createConversionDrafts: '',
        getConversionDraftsFee: '',
        createConversionFinalDrafts: '',
        getConversionFinalDraftsSummary: '',
        createGoldConversion: '',
        updateEarmarkDetails: '',
        createEarmarkConversion: '',
        getEarmarkConversionListing: '',
        getInventoryUsage: '',
        getSelectedCustomerConversion: '',
        // Stock Out
        getStockOutListing: '',
        createStockOut: '',
        getStockOutTransactionsListing: '',
        // market status
        getBranchMarketStatus: '',
    },
};

const goldSlice = createSlice({
    name: 'gold',
    initialState,
    reducers: {
        // ! buy and sell gold
        goldGetTransactionListingAttempt: (state) => {
            state.loadings.getTransactionListing = true;
            state.errors.getTransactionListing = '';
        },
        goldGetTransactionListingSuccess: (state, action: PayloadAction<TGetTransactionListingAPIResponse>) => {
            state.loadings.getTransactionListing = false;
            state.errors.getTransactionListing = '';
            state.buyAndSellGold.transactionListing = {
                filters: state.buyAndSellGold.transactionListing.filters,
                ...action.payload,
            };
        },
        goldGetTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionListing = false;
            if (action.payload) state.errors.getTransactionListing = action.payload;
        },
        goldGetGoldPricesAttempt: (state) => {
            state.loadings.getGoldPrices = true;
            state.errors.getGoldPrices = '';
        },
        goldGetGoldPricesSuccess: (state, action: PayloadAction<TGetGoldPricesAPIResponse>) => {
            state.loadings.getGoldPrices = false;
            state.errors.getGoldPrices = '';
            state.buyAndSellGold.goldPrices = action.payload;
        },
        goldGetGoldPricesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getGoldPrices = false;
            if (action.payload) state.errors.getGoldPrices = action.payload;
        },
        goldCreateBuyGoldTransactionAttempt: (state) => {
            state.loadings.createBuyGoldTransaction = true;
            state.errors.createBuyGoldTransaction = '';
        },
        goldCreateBuyGoldTransactionSuccess: (state) => {
            state.loadings.createBuyGoldTransaction = false;
            state.errors.createBuyGoldTransaction = '';
        },
        goldCreateBuyGoldTransactionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createBuyGoldTransaction = false;
            if (action.payload) state.errors.createBuyGoldTransaction = action.payload;
        },
        goldCreateSellGoldTransactionAttempt: (state) => {
            state.loadings.createSellGoldTransaction = true;
            state.errors.createSellGoldTransaction = '';
        },
        goldCreateSellGoldTransactionSuccess: (state) => {
            state.loadings.createSellGoldTransaction = false;
            state.errors.createSellGoldTransaction = '';
        },
        goldCreateSellGoldTransactionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createSellGoldTransaction = false;
            if (action.payload) state.errors.createSellGoldTransaction = action.payload;
        },
        goldGetTransactionReceiptAttempt: (state, _action: PayloadAction<{ transactionId: number }>) => {
            state.loadings.getTransactionReceipt = true;
            state.errors.getTransactionReceipt = '';
        },
        goldGetTransactionReceiptSuccess: (state) => {
            state.loadings.getTransactionReceipt = false;
            state.errors.getTransactionReceipt = '';
        },
        goldGetTransactionReceiptFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionReceipt = false;
            if (action.payload) state.errors.getTransactionReceipt = action.payload;
        },
        goldSetTransactionListingFilters: (state, action: PayloadAction<TGetTransactionListingFilters>) => {
            state.buyAndSellGold.transactionListing.filters = action.payload;
        },
        goldSetTransactionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.buyAndSellGold.transactionListing.index = index;
        },
        goldSetTransactionType: (state, action: PayloadAction<TransactionActionEnum>) => {
            state.buyAndSellGold.transactionType = action.payload;
        },
        goldSetSelectedTransaction: (state, action: PayloadAction<TTransactionListing | undefined>) => {
            state.buyAndSellGold.selectedTransaction = action.payload;
        },

        // ! gold inventory
        goldGetInventoryTransactionListingAttempt: (state) => {
            state.loadings.getInventoryTransactionListing = true;
            state.errors.getInventoryTransactionListing = '';
        },
        goldGetInventoryTransactionListingSuccess: (state, action: PayloadAction<TGetInventoryTransactionListingAPIResponse>) => {
            state.loadings.getInventoryTransactionListing = false;
            state.errors.getInventoryTransactionListing = '';
            state.goldInventory.inventoryTransactionListing = {
                filters: state.goldInventory.inventoryTransactionListing.filters,
                ...action.payload,
            };
        },
        goldGetInventoryTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryTransactionListing = false;
            if (action.payload) state.errors.getInventoryTransactionListing = action.payload;
        },
        goldGetInventoryListingAttempt: (state) => {
            state.loadings.getInventoryListing = true;
            state.errors.getInventoryListing = '';
        },
        goldGetInventoryListingSuccess: (state, action: PayloadAction<TGetInventoryListingAPIResponse>) => {
            state.loadings.getInventoryListing = false;
            state.errors.getInventoryListing = '';
            state.goldInventory.inventoryListing = {
                filters: state.goldInventory.inventoryListing.filters,
                selectedTab: state.goldInventory.inventoryListing.selectedTab,
                ...action.payload,
            };
        },
        goldGetInventoryListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryListing = false;
            if (action.payload) state.errors.getInventoryListing = action.payload;
        },
        goldCreateInventoryAttempt: (state) => {
            state.loadings.createInventory = true;
            state.errors.createInventory = '';
        },
        goldCreateInventorySuccess: (state) => {
            state.loadings.createInventory = false;
            state.errors.createInventory = '';
        },
        goldCreateInventoryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createInventory = false;
            if (action.payload) state.errors.createInventory = action.payload;
        },
        goldTransferInventoryAttempt: (state) => {
            state.loadings.transferInventory = true;
            state.errors.transferInventory = '';
        },
        goldTransferInventorySuccess: (state) => {
            state.loadings.transferInventory = false;
            state.errors.transferInventory = '';
        },
        goldTransferInventoryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.transferInventory = false;
            if (action.payload) state.errors.transferInventory = action.payload;
        },

        goldGetAllProductsAttempt: (state) => {
            state.loadings.getAllProducts = true;
            state.errors.getAllProducts = '';
        },
        goldGetAllProductsSuccess: (state, action: PayloadAction<TGetProductFilterAPIResponse>) => {
            state.loadings.getAllProducts = false;
            state.errors.getAllProducts = '';
            state.allProducts = action.payload.data;
        },
        goldGetAllProductsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllProducts = false;
            if (action.payload) state.errors.getAllProducts = action.payload;
        },
        goldGetAllInventoriesAttempt: (state) => {
            state.loadings.getAllInventories = true;
            state.errors.getAllInventories = '';
        },
        goldGetAllInventoriesSuccess: (state, action: PayloadAction<TGetInventoryFilterAPIResponse>) => {
            state.loadings.getAllInventories = false;
            state.errors.getAllInventories = '';
            state.allInventories = action.payload;
        },
        goldGetAllInventoriesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllInventories = false;
            if (action.payload) state.errors.getAllInventories = action.payload;
        },
        goldGetAllSerialNumbersAttempt: (state) => {
            state.loadings.getAllSerialNumbers = true;
            state.errors.getAllSerialNumbers = '';
        },
        goldGetAllSerialNumbersSuccess: (state, action: PayloadAction<TGetSerialNumberFilterAPIResponse>) => {
            state.loadings.getAllSerialNumbers = false;
            state.errors.getAllSerialNumbers = '';
            state.allSerialNumbers = action.payload;
        },
        goldGetAllSerialNumbersFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllSerialNumbers = false;
            if (action.payload) state.errors.getAllSerialNumbers = action.payload;
        },
        goldClearAllSerialNumbers: (state) => {
            state.allSerialNumbers = [];
            state.errors.getAllSerialNumbers = '';
        },
        // for multiple products in bank sell and buy
        goldGetMultipleSerialNumbersAttempt: (state) => {
            state.loadings.getMultipleSerialNumbers = true;
            state.errors.getMultipleSerialNumbers = '';
        },
        goldGetMultipleSerialNumbersSuccess: (state, action: PayloadAction<TGetSerialNumberFilterAPIResponse>) => {
            state.loadings.getMultipleSerialNumbers = false;
            state.errors.getMultipleSerialNumbers = '';

            const incomingProductIds = new Set(action.payload.map((item) => item.productId));
            const retained = state.multipleSerialNumbers.filter((item) => !incomingProductIds.has(item.productId));
            state.multipleSerialNumbers = [...retained, ...action.payload];
        },
        goldGetMultipleSerialNumbersFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getMultipleSerialNumbers = false;
            if (action.payload) state.errors.getMultipleSerialNumbers = action.payload;
        },
        goldClearMultipleSerialNumbers: (state) => {
            state.multipleSerialNumbers = [];
            state.errors.getMultipleSerialNumbers = '';
        },
        goldGetBranchInventoriesAttempt: (state, _action: PayloadAction<{ branchCode: string }>) => {
            state.loadings.getBranchInventories = true;
            state.errors.getBranchInventories = '';
        },
        goldGetBranchInventoriesSuccess: (state, action: PayloadAction<TGetInventoryFilterAPIResponse>) => {
            state.loadings.getBranchInventories = false;
            state.errors.getBranchInventories = '';
            state.branchInventories = action.payload;
        },
        goldGetBranchInventoriesFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getBranchInventories = false;
            if (action.payload) state.errors.getBranchInventories = action.payload;
        },
        goldGetInventoryTransferListingAttempt: (state) => {
            state.loadings.getInventoryTransferListing = true;
            state.errors.getInventoryTransferListing = '';
        },
        goldGetInventoryTransferListingSuccess: (state, action: PayloadAction<TGetInventoryTransferListingAPIResponse>) => {
            state.loadings.getInventoryTransferListing = false;
            state.errors.getInventoryTransferListing = '';
            state.goldInventory.inventoryTransferListing = {
                filters: state.goldInventory.inventoryTransferListing.filters,
                ...action.payload,
            };
        },
        goldGetInventoryTransferListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryTransferListing = false;
            if (action.payload) state.errors.getInventoryTransferListing = action.payload;
        },
        // table filter and paginator
        goldSetInventoryTransactionListingFilters: (state, action: PayloadAction<TGetInventoryTransactionListingFilters>) => {
            state.goldInventory.inventoryTransactionListing.filters = action.payload;
        },
        goldSetInventoryTransactionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.goldInventory.inventoryTransactionListing.index = index;
        },
        goldSetInventoryListingFilters: (state, action: PayloadAction<TGetInventoryListingFilters>) => {
            state.goldInventory.inventoryListing.filters = action.payload;
        },
        goldClearGetInventoryListingFilters: (state) => {
            state.goldInventory.inventoryListing.filters = initialState.goldInventory.inventoryListing.filters;
        },
        goldSetInventoryListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.goldInventory.inventoryListing.index = index;
        },
        goldSetSelectedInventoryTab: (state, action: PayloadAction<GoldSilverInventorySelectedTab>) => {
            state.goldInventory.inventoryListing.selectedTab = action.payload;
        },
        goldSetInventoryTransfersListingFilters: (state, action: PayloadAction<TGetInventoryTransferListingFilters>) => {
            state.goldInventory.inventoryTransferListing.filters = action.payload;
        },
        goldClearGetinventoryTransferListingFilters: (state) => {
            state.goldInventory.inventoryTransferListing.filters = initialState.goldInventory.inventoryTransferListing.filters;
        },
        //params
        goldSetBuyOrSellGoldParams: (state, action: PayloadAction<TBuyOrSellMultipleGoldParams>) => {
            state.params.buyOrSellGoldParams = action.payload;
        },
        goldClearSellOrBuyGoldParams: (state) => {
            state.params.buyOrSellGoldParams = initialState.params.buyOrSellGoldParams;
        },
        goldSetCreateInventoryParams: (state, action: PayloadAction<TCreateInventoryParamsV2>) => {
            state.params.createInventoryParams = action.payload;
        },
        goldClearCreateInventoryParams: (state) => {
            state.params.createInventoryParams = initialState.params.createInventoryParams;
        },
        goldSetTransferInventoryParams: (state, action: PayloadAction<TTransferInventoryParams>) => {
            state.params.transferInventoryParams = action.payload;
        },
        goldClearTransferInventoryParams: (state) => {
            state.params.transferInventoryParams = initialState.params.transferInventoryParams;
        },
        goldSetInventoryFilterParams: (state, action: PayloadAction<TGetInventoryFilterParams>) => {
            state.params.inventoryFilterParams = action.payload;
        },
        goldClearInventoryFilterParams: (state) => {
            state.params.inventoryFilterParams = initialState.params.inventoryFilterParams;
        },
        goldSetSerialNumberFilterParams: (state, action: PayloadAction<TGetSerialNumberFilterParams>) => {
            state.params.serialNumberFilterParams = action.payload;
        },
        goldClearSerialNumberFilterParams: (state) => {
            state.params.serialNumberFilterParams = initialState.params.serialNumberFilterParams;
        },
        goldSetGoldConversionParams: (state, action: PayloadAction<TCreateGoldConversionReduxParams>) => {
            state.params.goldConversionParams = action.payload;
        },
        goldResetGoldConversionDraftsFee: (state) => {
            state.params.goldConversionDraftsFeeParams = initialState.params.goldConversionDraftsFeeParams;
        },
        goldResetGoldConversionFinalDrafts: (state) => {
            state.params.goldConversionFinalDraftsParams = initialState.params.goldConversionFinalDraftsParams;
        },
        goldClearGoldConversionParams: (state) => {
            state.params.goldConversionParams = initialState.params.goldConversionParams;
        },
        // modals
        goldSetIsFormFilled: (state, action: PayloadAction<boolean>) => {
            state.modals.isFormFilled = action.payload;
        },
        goldSetIsConfirmationModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isConfirmationModalOpen = action.payload;
        },
        goldSetIsBuyOrSellGoldModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isBuyOrSellGoldModalOpen = action.payload;
        },
        goldSetIsTransactionDetailsModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isTransactionDetailsModalOpen = action.payload;
        },
        goldSetIsInventoryListingModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isInventoryListingModalOpen = action.payload;
        },
        goldSetIsCreateInventoryModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isCreateInventoryModalOpen = action.payload;
        },
        goldSetIsTransferInventoryModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isTransferInventoryModalOpen = action.payload;
        },
        goldSetIsConversionDetailsModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isConversionDetailsModalOpen = action.payload;
        },

        goldExportInventoryTransactionListingAttempt: (state) => {
            state.loadings.exportInventoryTransactionListing = true;
            state.errors.exportInventoryTransactionListing = '';
        },
        goldExportInventoryTransactionListingSuccess: (state, action: PayloadAction<TInventoryTransactionListing[]>) => {
            state.loadings.exportInventoryTransactionListing = false;
            state.inventoryTransactionListingForExport = action.payload;
        },
        goldExportInventoryTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryTransactionListing = false;
            if (action.payload) state.errors.exportInventoryTransactionListing = action.payload;
        },
        goldClearInventoryTransactionListingForExport: (state) => {
            state.inventoryTransactionListingForExport = initialState.inventoryTransactionListingForExport;
        },
        goldExportGoldTransactionListingAttempt: (state) => {
            state.loadings.exportGoldTransactionListing = true;
            state.errors.exportGoldTransactionListing = '';
        },
        goldExportGoldTransactionListingSuccess: (state, action: PayloadAction<TGoldTransactionListing[]>) => {
            state.loadings.exportGoldTransactionListing = false;
            state.goldTransactionListingForExport = action.payload;
        },
        goldExportGoldTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportGoldTransactionListing = false;
            if (action.payload) state.errors.exportGoldTransactionListing = action.payload;
        },
        goldClearGoldTransactionListingForExport: (state) => {
            state.goldTransactionListingForExport = initialState.goldTransactionListingForExport;
        },
        goldSetTempUpdateInventoryTransactionState: (state, action: PayloadAction<TUpdateInventoryTransactionStatusReduxParams>) => {
            state.tempUpdateInventoryTransactionState = action.payload;
        },
        goldSetConfirmUpdateInventoryTransactionStatusModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateInventoryTransactionStatusModalIsOpen = action.payload;
        },
        goldResetUpdateInventoryTransactionStatusState: (state) => {
            state.loadings.updateInventoryTransactionStatus = initialState.loadings.updateInventoryTransactionStatus;
            state.errors.updateInventoryTransactionStatus = initialState.errors.updateInventoryTransactionStatus;
        },
        goldUpdateInventoryTransactionStatusAttempt: (state, _action: PayloadAction<TUpdateInventoryTransactionStatusReduxParams>) => {
            state.loadings.updateInventoryTransactionStatus = true;
            state.errors.updateInventoryTransactionStatus = '';
        },
        goldUpdateInventoryTransactionStatusSuccess: (state) => {
            state.loadings.updateInventoryTransactionStatus = false;
        },
        goldUpdateInventoryTransactionStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateInventoryTransactionStatus = false;
            if (action.payload) state.errors.updateInventoryTransactionStatus = action.payload;
        },
        goldSetTempUpdateTransactionState: (state, action: PayloadAction<TUpdateTransactionStatusReduxParams>) => {
            state.tempUpdateTransactionState = action.payload;
        },
        goldSetConfirmUpdateTransactionStatusModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateTransactionStatusModalIsOpen = action.payload;
        },
        goldSetConfirmUpdateCancelTransactionModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateCancelTransactionModalIsOpen = action.payload;
        },
        goldResetUpdateTransactionStatusState: (state) => {
            state.loadings.updateTransactionStatus = initialState.loadings.updateTransactionStatus;
            state.errors.updateTransactionStatus = initialState.errors.updateTransactionStatus;
        },
        goldUpdateTransactionStatusAttempt: (state, _action: PayloadAction<TUpdateTransactionStatusReduxParams>) => {
            state.loadings.updateTransactionStatus = true;
            state.errors.updateTransactionStatus = '';
        },
        goldUpdateTransactionStatusSuccess: (state) => {
            state.loadings.updateTransactionStatus = false;
        },
        goldUpdateTransactionStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateTransactionStatus = false;
            if (action.payload) state.errors.updateTransactionStatus = action.payload;
        },
        goldGetTotalNetValueOrQuantityAttempt: (state, _action: PayloadAction<TGetTotalNetValueOrQuantityReduxParams>) => {
            state.loadings.getTotalNetValueOrQuantity = true;
            state.errors.getTotalNetValueOrQuantity = '';
        },

        goldGetTotalNetValueOrQuantitySuccess: (state, action: PayloadAction<{ totalNetValue?: string, quantity?: string, index: number }>) => {
            state.loadings.getTotalNetValueOrQuantity = false;

            if (action.payload.quantity) state.params.buyOrSellGoldParams.products[action.payload.index].amount = action.payload.quantity;
            if (action.payload.totalNetValue) state.params.buyOrSellGoldParams.products[action.payload.index].totalNetValue = action.payload.totalNetValue;
        },
        goldGetTotalNetValueOrQuantityFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTotalNetValueOrQuantity = false;
            if (action.payload) state.errors.getTotalNetValueOrQuantity = action.payload;
        },

        goldCreateTransactionDraftAttempt: (state, _action: PayloadAction<TCreateTransactionDraftReduxParams>) => {
            state.loadings.createTransactionDraft = true;
            state.errors.createTransactionDraft = '';
        },
        goldCreateTransactionDraftSuccess: (state, action: PayloadAction<TCreateTransactionDraftResponse>) => {
            state.createTransactionSummary = action.payload;
            state.loadings.createTransactionDraft = false;
            state.errors.createTransactionDraft = '';
        },

        goldCreateTransactionDraftFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createTransactionDraft = false;
            if (action.payload) state.errors.createTransactionDraft = action.payload;
        },
        goldClearCreateTransactionDraftError: (state) => {
            state.errors.createTransactionDraft = '';
        },

        goldSetTempUpdateInventoryTransferState: (state, action: PayloadAction<TupdateTransferStatusReduxParams>) => {
            state.tempUpdateInventoryTransfersState = action.payload;
        },
        goldSetConfirmUpdateInventoryTransferStatusModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmUpdateInventoryTransfersStatusModalIsOpen = action.payload;
        },
        goldResetUpdateInventoryTransferStatusState: (state) => {
            state.loadings.updateInventoryTransferStatus = initialState.loadings.updateInventoryTransferStatus;
            state.errors.updateInventoryTransferStatus = initialState.errors.updateInventoryTransferStatus;
        },
        goldUpdateInventoryTransferStatusAttempt: (state, _action: PayloadAction<TupdateTransferStatusReduxParams>) => {
            state.loadings.updateInventoryTransferStatus = true;
            state.errors.updateInventoryTransferStatus = '';
        },
        goldUpdateInventoryTransferStatusSuccess: (state) => {
            state.loadings.updateInventoryTransferStatus = false;
        },
        goldUpdateInventoryTransferStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateInventoryTransferStatus = false;
            if (action.payload) state.errors.updateInventoryTransferStatus = action.payload;
        },
        goldSetTransactionProductListingModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.transactionProductListingModalIsOpen = action.payload;
        },
        goldSetConversionListingModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.conversionListingDetailsModalIsOpen = action.payload;
        },
        goldSetSerialNumberListingFilters: (state, action: PayloadAction<TGetSerialNumberListingFilter>) => {
            state.serialNumber.serialNumberListing.filters = action.payload;
        },
        goldSetSerialNumberListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.serialNumber.serialNumberListing.index = index;
        },
        goldGetSerialNumberListingAttempt: (state) => {
            state.loadings.getSerialNumberListing = true;
            state.errors.getSerialNumberListing = '';
        },
        goldGetSerialNumberListingSuccess: (state, action: PayloadAction<TGetSerialNumberListingAPIResponse>) => {
            state.loadings.getSerialNumberListing = false;
            state.serialNumber.serialNumberListing = {
                filters: state.serialNumber.serialNumberListing.filters,
                ...action.payload,
            };
        },
        goldGetSerialNumberListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSerialNumberListing = false;
            if (action.payload) state.errors.getSerialNumberListing = '';
        },
        goldGetTransactionPricePerGramValueAttempt: (state, _action: PayloadAction<TGetTransactionPricePerGramValueReduxParam>) => {
            state.loadings.getTransactionPricePerGramValue = true;
            state.errors.getTransactionPricePerGramValue = '';
        },
        goldGetTransactionPricePerGramValueSuccess: (state, action: PayloadAction<{pricePerGram: string | null, index: number}>) => {
            state.loadings.getTransactionPricePerGramValue = false;
            if (action.payload) state.params.buyOrSellGoldParams.products[action.payload.index].goldAmountMYR = action.payload.pricePerGram || '';
        },
        goldGetTransactionPricePerGramValueFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionPricePerGramValue = false;
            if (action.payload) state.errors.getTransactionPricePerGramValue = action.payload;
        },
        goldResetTransactionPricePerGramValue: (state, action: PayloadAction<{ index: number}>) => {
            state.params.buyOrSellGoldParams.products[action.payload.index].goldAmountMYR = '';
        },
        // Gold Inventory Report
        goldSetSelectedGoldInventoryReportTab: (state, action: PayloadAction<ReportsGoldInventorySelectedTab>) => {
            state.goldInventoryReport.selectedTab = action.payload;
        },
        goldSetInventoryReceivedListingFilters: (state, action: PayloadAction<ReportGoldInventoryReceivedListingFilters>) => {
            state.goldInventoryReport.inventoryReceived.filters = action.payload;
        },
        goldClearInventoryReceivedListingFilters: (state) => {
            state.goldInventoryReport.inventoryReceived.filters = initialState.goldInventoryReport.inventoryReceived.filters;
        },
        goldGetGoldInventoryReportReceivedListingAttempt: (state) => {
            state.loadings.getGoldInventoryReportReceivedListing = true;
            state.errors.getGoldInventoryReportReceivedListing = '';
        },
        goldGetGoldInventoryReportReceivedListingSuccess: (state, action: PayloadAction<TGetInventoryTransfersAPIResponse>) => {
            state.loadings.getGoldInventoryReportReceivedListing = false;
            state.errors.getGoldInventoryReportReceivedListing = '';
            state.goldInventoryReport.inventoryReceived = {
                filters: state.goldInventoryReport.inventoryReceived.filters,
                ...action.payload,
            };
        },
        goldGetGoldInventoryReportReceivedListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getGoldInventoryReportReceivedListing = false;
            if (action.payload) state.errors.getGoldInventoryReportReceivedListing = '';
        },
        goldSetGoldInventoryReportReceivedListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.goldInventoryReport.inventoryReceived.index = index;
        },
        goldExportInventoryReceivedListingAttempt: (state) => {
            state.loadings.exportInventoryReceivedListing = true;
            state.errors.exportInventoryReceivedListing = '';
        },
        goldExportInventoryReceivedListingSuccess: (state, action: PayloadAction<TInventoryTransferData[]>) => {
            state.loadings.exportInventoryReceivedListing = false;
            state.inventoryReceivedListingForExport = action.payload;
        },
        goldExportInventoryReceivedListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryReceivedListing = false;
            if (action.payload) state.errors.exportInventoryReceivedListing = action.payload;
        },
        goldClearInventoryReceivedListingForExport: (state) => {
            state.inventoryReceivedListingForExport = initialState.inventoryReceivedListingForExport;
        },

        goldSetInventoryDeliveredListingFilters: (state, action: PayloadAction<ReportGoldInventoryReceivedListingFilters>) => {
            state.goldInventoryReport.inventoryDelivered.filters = action.payload;
        },
        goldGetGoldInventoryReportDeliveredListingAttempt: (state) => {
            state.loadings.getGoldInventoryReportDeliveredListing = true;
            state.errors.getGoldInventoryReportDeliveredListing = '';
        },
        goldGetGoldInventoryReportDeliveredListingSuccess: (state, action: PayloadAction<TGetInventoryTransfersAPIResponse>) => {
            state.loadings.getGoldInventoryReportDeliveredListing = false;
            state.errors.getGoldInventoryReportDeliveredListing = '';
            state.goldInventoryReport.inventoryDelivered = {
                filters: state.goldInventoryReport.inventoryDelivered.filters,
                ...action.payload,
            };
        },
        goldGetGoldInventoryReportDeliveredListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getGoldInventoryReportDeliveredListing = false;
            if (action.payload) state.errors.getGoldInventoryReportDeliveredListing = '';
        },
        goldSetGoldInventoryReportDeliveredListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.goldInventoryReport.inventoryDelivered.index = index;
        },
        goldExportInventoryDeliveredListingAttempt: (state) => {
            state.loadings.exportInventoryDeliveredListing = true;
            state.errors.exportInventoryDeliveredListing = '';
        },
        goldExportInventoryDeliveredListingSuccess: (state, action: PayloadAction<TInventoryTransferData[]>) => {
            state.loadings.exportInventoryDeliveredListing = false;
            state.inventoryDeliveredListingForExport = action.payload;
        },
        goldExportInventoryDeliveredListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryDeliveredListing = false;
            if (action.payload) state.errors.exportInventoryDeliveredListing = action.payload;
        },
        goldClearInventoryDeliveredListingForExport: (state) => {
            state.inventoryDeliveredListingForExport = initialState.inventoryDeliveredListingForExport;
        },
        goldGetBranchInventoryListingAttempt: (state) => {
            state.loadings.getBranchInventoryListing = true;
            state.errors.getBranchInventoryListing = '';
        },
        goldGetBranchInventoryListingSuccess: (state, action: PayloadAction<TGetInventoryListingAPIResponse>) => {
            state.loadings.getBranchInventoryListing = false;
            state.goldInventoryReport.branchInventory = {
                filters: state.goldInventoryReport.branchInventory.filters,
                ...action.payload,
            };
        },
        goldGetBranchInventoryListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getBranchInventoryListing = false;
            if (action.payload) state.errors.getBranchInventoryListing = action.payload;
        },
        goldSetBranchInventoryListingFilters: (state, action: PayloadAction<{branchName: string, branchCode: string}>) => {
            state.goldInventoryReport.branchInventory.filters = action.payload;
        },
        goldClearBranchInventoryListingFilters: (state) => {
            state.goldInventoryReport.branchInventory.filters = initialState.goldInventoryReport.branchInventory.filters;
        },
        goldSetIsCreateGoldConversionModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isCreateGoldConversionModalOpen = action.payload;
        },
        goldSetIsCreateStockOutModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isCreateStockOutModalOpen = action.payload;
        },
        goldSetGoldConversionIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.goldConversion.index = action.payload.index;
        },
        goldSetGoldConversionFilters: (state, action: PayloadAction<TGoldConversionFiltersRedux>) => {
            state.goldConversion.filters = action.payload;
        },
        goldGetGoldConversionListingAttempt: (state, action: PayloadAction<{ customerId?: number } | undefined>,) => {
            state.loadings.getGoldConversionListing = true;
            state.errors.getGoldConversionListing = '';
        },
        goldGetGoldConversionListingSuccess: (state, action: PayloadAction<TGoldConversionListingAPIResponse>) => {
            state.loadings.getGoldConversionListing = false;
            state.goldConversion = {
                filters: state.goldConversion.filters,
                ...action.payload,
            };
        },
        goldGetGoldConversionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getGoldConversionListing = false;
            if (action.payload) state.errors.getGoldConversionListing = action.payload;
        },
        goldCreateGoldConversionDraftsAttempt: (state, action: PayloadAction<{ metalType: MetalTypeEnum }>) => {
            state.loadings.createConversionDrafts = true;
            state.errors.createConversionDrafts = '';
        },
        goldCreateGoldConversionDraftsSuccess: (state, action: PayloadAction<TCreateConversionDraftsAPIResponse>) => {
            state.loadings.createConversionDrafts = false;
            state.errors.createConversionDrafts = '';
            state.goldConversionDraftId = action.payload;
        },
        goldCreateGoldConversionDraftsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createConversionDrafts = false;
            if (action.payload) state.errors.createConversionDrafts = action.payload;
        },
        goldGetGoldConversionDraftsFeeAttempt: (state, action: PayloadAction<TGetConversionDraftsFeeReduxParams>) => {
            state.loadings.getConversionDraftsFee = true;
            state.errors.getConversionDraftsFee = '';
        },
        goldGetConversionDraftsFeeSuccess: (state, action: PayloadAction<TGetConversionDraftsFeeAPIResponse>) => {
            state.loadings.getConversionDraftsFee = false;
            state.goldConversionDraftsFee = action.payload;
        },
        goldGetConversionDraftsFeeFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getConversionDraftsFee = false;
            if (action.payload) state.errors.getConversionDraftsFee = action.payload;
        },
        goldClearConversionDraftsFee: state => {
            state.goldConversionDraftsFee = {
                conversionFeePerGram: 0,
                weightedConversionFee: 0,
            };
            state.loadings.getConversionDraftsFee = false;
            state.errors.getConversionDraftsFee = '';
        },
        goldCreateGoldConversionFinalDraftsAttempt: (state, action: PayloadAction<TCreateConversionFinalDraftsParams>) => {
            state.loadings.createConversionFinalDrafts = true;
            state.errors.createConversionFinalDrafts = '';
        },
        goldCreateConversionFinalDraftsSuccess: (state, action: PayloadAction<TCreateConversionFinalDraftsParams>) => {
            state.loadings.createConversionFinalDrafts = false;
            state.errors.createConversionFinalDrafts = '';

            state.params.goldConversionFinalDraftsParams = action.payload;
        },
        goldCreateConversionFinalDraftsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createConversionFinalDrafts = false;
            if (action.payload) state.errors.createConversionFinalDrafts = action.payload;
        },
        goldGetGoldConversionFinalDraftsSummaryAttempt: (state, action: PayloadAction<{ id: number }>) => {
            state.loadings.getConversionFinalDraftsSummary = true;
            state.errors.getConversionFinalDraftsSummary = '';
        },
        goldGetConversionFinalDraftsSummarySuccess: (state, action: PayloadAction<TGetConversionFinalDraftsSummaryAPIResponse>) => {
            state.loadings.getConversionFinalDraftsSummary = false;
            state.errors.getConversionFinalDraftsSummary = '';

            state.goldConversionFinalDraftsSummary = action.payload;
        },
        goldGetConversionFinalDraftsSummaryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getConversionFinalDraftsSummary = false;
            if (action.payload) state.errors.getConversionFinalDraftsSummary = action.payload;
        },
        goldCreateGoldConversionAttempt: (state, _action: PayloadAction<TCreateGoldConversionReduxParams>) => {
            state.loadings.createGoldConversion = true;
            state.errors.createGoldConversion = '';
        },
        goldCreateGoldConversionSuccess: (state) => {
            state.loadings.createGoldConversion = false;
            state.errors.createGoldConversion = '';
        },
        goldCreateGoldConversionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createGoldConversion = false;
            if (action.payload) state.errors.createGoldConversion = action.payload;
        },
        goldGetGoldConversionAmountAttempt: (state, _action: PayloadAction<TGetGoldConversionAmountParams>) => {
            state.loadings.getGoldConversionAmount = true;
            state.errors.getGoldConversionAmount = '';
        },
        goldGetGoldConversionAmountSuccess: (state, action: PayloadAction<number>) => {
            state.loadings.getGoldConversionAmount = false;
            if (action.payload) state.goldConversion.goldConversionAmount = action.payload;
        },
        goldGetGoldConversionAmountFailure: (state, action: PayloadAction<string | null>) => {
            state.loadings.getGoldConversionAmount = false;
            if (action.payload) state.errors.getGoldConversionAmount = action.payload;
        },
        goldResetGoldConversionAmount: (state) => {
            state.goldConversion.goldConversionAmount = initialState.goldConversion.goldConversionAmount;
        },
        goldSetSelectedConversion: (state, action: PayloadAction<TGoldConversionListingData | undefined>) => {
            state.selectedConversion = action.payload;
        },
        goldSetSelectedCustomerConversion: (state, action: PayloadAction<TGoldConversionListingData | undefined>) => {
            state.selectedCustomerConversionDetails = action.payload;
        },

        goldCreateEarmarkConversionAttempt: (state, _action: PayloadAction<TCreateEarmarkConversionReduxParams>) => {
            state.loadings.createEarmarkConversion = true;
            state.errors.createEarmarkConversion = '';
        },

        goldCreateEarmarkConversionSuccess: (state) => {
            state.loadings.createEarmarkConversion = false;
            state.errors.createEarmarkConversion = '';
        },

        goldCreateEarmarkConversionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createEarmarkConversion = false;
            if (action.payload) state.errors.createEarmarkConversion = action.payload;
        },

        goldGetEarmarkConversionListingAttempt: (state, action: PayloadAction<TGetEarmarkConversionListingReduxParams>) => {
            state.loadings.getEarmarkConversionListing = true;
            state.errors.getEarmarkConversionListing = '';
        },
        goldGetEarmarkConversionListingSuccess: (state, action: PayloadAction<TGetEarmarkConversionListingAPIResponse>) => {
            state.loadings.getEarmarkConversionListing = false;
            state.errors.getEarmarkConversionListing = '';
            state.earmarkConversionListing = {
                filters: state.earmarkConversionListing.filters,
                ...action.payload,
            };
        },
        goldGetEarmarkConversionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getEarmarkConversionListing = false;
            if (action.payload) state.errors.getEarmarkConversionListing = action.payload;
        },
        goldSetEarmarkConversionListingFilters: (state, action: PayloadAction<TGetEarmarkConversionListingFilters>) => {
            state.earmarkConversionListing.filters = action.payload;
        },
        goldSetEarmarkConversionListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.earmarkConversionListing.index = action.payload.index;
        },
        goldUpdateEarmarkDetailsAttempt: (state, _action: PayloadAction<TUpdateEarmarkDetailsReduxParam>) => {
            state.loadings.updateEarmarkDetails = true;
            state.errors.updateEarmarkDetails = '';
        },
        goldUpdateEarmarkDetailsSuccess: (state) => {
            state.loadings.updateEarmarkDetails = false;
        },
        goldUpdateEarmarkDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateEarmarkDetails = false;
            if (action.payload) state.errors.updateEarmarkDetails = action.payload;
        },
        goldGetInventoryUsageAttempt: (state, _action: PayloadAction<{ branchId?: number } | undefined>) => {
            state.loadings.getInventoryUsage = true;
            state.errors.getInventoryUsage = '';
        },
        goldGetInventoryUsageSuccess: (state, action: PayloadAction<TGetInventoryUsageAPIResponse>) => {
            state.loadings.getInventoryUsage = false;
            state.errors.getInventoryUsage = '';
            state.inventoryUsage = {
                ...state.inventoryUsage,
                ...action.payload,
            };
        },
        goldGetInventoryUsageFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryUsage = false;
            if (action.payload) state.errors.getInventoryUsage = action.payload;
        },
        goldGetStockOutsAttempt: (state, _action: PayloadAction<{ branchId?: number } | undefined>) => {
            state.loadings.getStockOutListing = true;
            state.errors.getStockOutListing = '';
        },
        goldGetStockOutsSuccess: (state, action: PayloadAction<TGetStockOutsAPIResponse>) => {
            state.loadings.getStockOutListing = false;
            state.errors.getStockOutListing = '';
            state.goldInventory.inventoryStockOutListing = {
                filters: state.goldInventory.inventoryStockOutListing.filters,
                ...action.payload,
            };
        },
        goldGetStockOutsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getStockOutListing = false;
            if (action.payload) state.errors.getStockOutListing = action.payload;
        },
        goldSetStockOutsFilters: (state, action: PayloadAction<TGetStockOutsListingFilters>) => {
            state.goldInventory.inventoryStockOutListing.filters = action.payload;
        },
        goldCreateStockOutAttempt: (state) => {
            state.loadings.createStockOut = true;
            state.errors.createStockOut = '';
        },
        goldCreateStockOutSuccess: (state) => {
            state.loadings.createStockOut = false;
            state.errors.createStockOut = '';
        },
        goldCreateStockOutFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createStockOut = false;
            if (action.payload) state.errors.createStockOut = action.payload;
        },
        goldSetCreateStockOutParams: (state, action: PayloadAction<TCreateStockOutParams>) => {
            state.params.createStockOutParams = action.payload;
        },
        goldClearCreateStockOutParams: (state) => {
            state.params.createStockOutParams = initialState.params.createStockOutParams;
        },
        goldGetInventoryStockListingAttempt: (state) => {
            state.loadings.getInventoryListing = true;
            state.errors.getInventoryListing = '';
        },
        goldGetInventoryStockListingSuccess: (state, action: PayloadAction<TGetInventoryListingAPIResponse>) => {
            state.loadings.getInventoryListing = false;
            state.errors.getInventoryListing = '';
            state.goldInventory.inventoryListing = {
                filters: state.goldInventory.inventoryListing.filters,
                selectedTab: state.goldInventory.inventoryListing.selectedTab,
                ...action.payload,
            };
        },
        goldGetInventoryStockListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryListing = false;
            if (action.payload) state.errors.getInventoryListing = action.payload;
        },
        goldGetBranchMarketStatusAttempt: (state, _action: PayloadAction<{ branchCode?: string }>) => {
            state.loadings.getBranchMarketStatus = true;
            state.errors.getBranchMarketStatus = '';
        },
        goldGetBranchMarketStatusSuccess: (state, action: PayloadAction<TGetBranchMarketStatusBranchAPIResponse>) => {
            state.loadings.getBranchMarketStatus = false;
            state.errors.getBranchMarketStatus = '';
            state.goldBranchMarketStatusDetails = action.payload;
        },
        goldGetBranchMarketStatusFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getBranchMarketStatus = false;
            if (action.payload) state.errors.getBranchMarketStatus = action.payload;
        },
        goldGetStockOutsTransactionsAttempt: (state, _action: PayloadAction<{ branchId?: number } | undefined>) => {
            state.loadings.getStockOutTransactionsListing = true;
            state.errors.getStockOutTransactionsListing = '';
        },
        goldGetStockOutsTransactionsSuccess: (state, action: PayloadAction<TgetStockOutTransactionsListingAPIResponse>) => {
            state.loadings.getStockOutTransactionsListing = false;
            state.errors.getStockOutTransactionsListing = '';

            state.goldInventory.inventoryStockOutTransactionsListing = {
                ...action.payload,
            };
        },
        goldGetStockOutsTransactionsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getStockOutTransactionsListing = false;
            if (action.payload) state.errors.getStockOutTransactionsListing = action.payload;
        },
        goldSetStockOutsTransactionsFilters: (state, action: PayloadAction<TGetStockOutTransactionsListingParams>) => {
            state.goldInventory.inventoryStockOutTransactionsListing.filters = action.payload;
        },
        goldSetIsStockOutTransactionsModalOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.isStockOutTransactionsModalOpen = action.payload;
        },
        goldGetCancelAffectedTransactionAttempt: (state, _action: PayloadAction<{ transactionId?: number } | undefined>) => {
            state.loadings.getCancelAffectedTransaction = true;
            state.errors.getCancelAffectedTransaction = '';
        },
        goldGetCancelAffectedTransactionSuccess: (state, action: PayloadAction<TGetCancelAffectedTransactionAPIResponse>) => {
            state.loadings.getCancelAffectedTransaction = false;
            state.errors.getCancelAffectedTransaction = '';
            state.buyAndSellGold.cancelAffectedTransaction = action.payload;
        },
        goldGetCancelAffectedTransactionFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCancelAffectedTransaction = false;
            if (action.payload) state.errors.getCancelAffectedTransaction = action.payload;
        },
        goldGetTransactionBankBuySerialNumberDetailsAttempt: (state, _action: PayloadAction<TGetTransactionBankBuySerialNumberDetailsBatchParams>) => {
            state.loadings.getTransactionBankBuySerialNumberDetails = true;
            state.errors.getTransactionBankBuySerialNumberDetails = '';
        },
        goldGetTransactionBankBuySerialNumberDetailsSuccess: (state, action: PayloadAction<TGetTransactionBankBuySerialNumberDetailsAPIResponse[]>) => {
            state.loadings.getTransactionBankBuySerialNumberDetails = false;
            state.errors.getTransactionBankBuySerialNumberDetails = '';
        },
        goldGetTransactionBankBuySerialNumberDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getTransactionBankBuySerialNumberDetails = false;
            if (action.payload) state.errors.getTransactionBankBuySerialNumberDetails = action.payload;
        },
    },
});

export default {
    actions: goldSlice.actions,
    reducers: goldSlice.reducer,
};
